import { Component, Inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, filter, Subject, switchMap, takeUntil } from 'rxjs';
import { UserAdminService } from '../user-admin.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';

@Component({
    selector: 'gtw-user-assign-modal',
    templateUrl: './user-assign-modal.component.html',
    styleUrls: ['./user-assign-modal.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserAssignModalComponent implements OnInit, OnDestroy {
  title = 'Assigned Users';
  assignedUsers: any[] = [];
  description = '';
  searchText = '';
  searchResults: any[] = [];
  searching = false;
  noResults = false;
  saving = false;

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private taskKey: any;

  constructor(
    public dialogRef: MatDialogRef<UserAssignModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userAdminService: UserAdminService,
    private alertService: GtwSnackbarService
  ) {
    this.taskKey = data?.taskKey;
  }

  ngOnInit(): void {
    this.loadAssignedUsers();

    this.searchSubject.pipe(
      debounceTime(300),
      filter((val: string) => val.length >= 2),
      switchMap((val: string) => {
        this.searching = true;
        this.noResults = false;
        return this.userAdminService.searchUsers(val);
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      (response: any) => {
        this.searching = false;
        if (response?.jsonObject?.length) {
          this.searchResults = response.jsonObject;
          this.noResults = false;
        } else {
          this.searchResults = [];
          this.noResults = true;
        }
      },
      () => {
        this.searching = false;
        this.searchResults = [];
      }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadAssignedUsers(): void {
    this.userAdminService.getTaskOwner(this.taskKey).pipe(takeUntil(this.destroy$)).subscribe(
      (response: any) => {
        if (response?.callSuccess === '1' && response.taskOwnerList) {
          this.assignedUsers = response.taskOwnerList.map((item: any) => ({
            NAME: item.name,
            VALUE: item.value
          }));
        }
      },
      () => {
        this.alertService.openSnackBar('Failed to fetch assigned user data', 'gtw-snackbar--red');
      }
    );
  }

  onSearch(): void {
    this.searchResults = [];
    this.noResults = false;
    if (this.searchText.length < 2) {
      this.searching = false;
      return;
    }
    this.searchSubject.next(this.searchText);
  }

  selectUser(user: any): void {
    const alreadyAssigned = this.assignedUsers.some(
      (u: any) => (u.VALUE || u.value) === (user.VALUE || user.value)
    );
    if (!alreadyAssigned) {
      this.assignedUsers.push(user);
    }
    this.searchText = '';
    this.searchResults = [];
    this.noResults = false;
  }

  removeUser(user: any, index: number): void {
    const ssoId = user.VALUE || user.value;
    this.userAdminService.removeTaskOwner(this.taskKey, ssoId).pipe(takeUntil(this.destroy$)).subscribe(
      (response: any) => {
        if (response?.callSuccess === '1') {
          this.assignedUsers.splice(index, 1);
          this.alertService.openSnackBar('Record deleted successfully.', 'gtw-snackbar--green');
        } else {
          this.alertService.openSnackBar('Failed to delete the data.', 'gtw-snackbar--red');
        }
      },
      () => {
        this.alertService.openSnackBar('Failed to delete the data.', 'gtw-snackbar--red');
      }
    );
  }

  save(): void {
    this.saving = true;
    this.userAdminService.saveModuleOwner(this.taskKey, this.assignedUsers, this.description).pipe(takeUntil(this.destroy$)).subscribe(
      (response: any) => {
        this.saving = false;
        if (response?.callSuccess === '1') {
          this.alertService.openSnackBar('User has been added successfully', 'gtw-snackbar--green');
          this.dialogRef.close({ saved: true });
        } else {
          this.alertService.openSnackBar(response?.errorMessage || 'Error saving data', 'gtw-snackbar--yellow');
        }
      },
      () => {
        this.saving = false;
        this.alertService.openSnackBar('Error saving data', 'gtw-snackbar--red');
      }
    );
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
